/**
 * Helpers for the IPO News page (/ipo-news). Takes the articles mapped by
 * utils/newsMapper.js (GET /api/market/news) and adds the desk's lifecycle
 * tabs, an IPO-relevance check, search, sorting and date formatting.
 *
 * The feed has no category field, so each story's tabs come from its headline
 * and summary. A story can sit under several tabs; its badge shows the most
 * specific one.
 */

// Tab order as shown on the page; `badge` styles the story's category label
export const NEWS_DESK_TABS = [
  { key: 'all', label: 'All News' },
  {
    key: 'confirmed',
    label: 'Confirmed IPO',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    pattern: /\b(price band|set to open|opens? (on|today|tomorrow)|to open on|ipo dates?|lot size|launch(es)? on|bidding (opens|starts))\b/i,
  },
  {
    key: 'subscription',
    label: 'IPO Subscription',
    badge: 'bg-sky-50 text-sky-700 border-sky-200',
    pattern: /\bsubscri\w*|\bbids?\b|\bbidding\b|\bday [1-3]\b|\bqibs?\b|\bdemand\b/i,
  },
  {
    key: 'post_listed',
    label: 'Post Listed IPO',
    badge: 'bg-violet-50 text-violet-700 border-violet-200',
    pattern: /\b(post[- ]listing|after (its )?listing|since (its )?listing|shares? (jumps?|rises?|falls?|surges?|slumps?|tanks?)|stock (jumps?|rises?|falls?)|target price)\b/i,
  },
  {
    key: 'allotment',
    label: 'IPO Allotment',
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    pattern: /\ballot(ment|ted)?\b/i,
  },
  {
    key: 'upcoming',
    label: 'Upcoming IPO',
    badge: 'bg-teal-50 text-[#006666] border-teal-200',
    pattern: /\b(upcoming|drhp|draft (red herring|papers|prospectus)|files? (for|papers)|filing|sebi (nod|approval|clears)|plans?|mulls?|seeks?|prepar\w*|eyes|to raise|gears? up)\b/i,
  },
  {
    key: 'closed',
    label: 'IPO Closed',
    badge: 'bg-slate-100 text-slate-600 border-slate-200',
    pattern: /\b(clos(es|ed|ing)|last day|final day)\b/i,
  },
  {
    key: 'listed',
    label: 'Listed IPO',
    badge: 'bg-rose-50 text-rose-600 border-rose-200',
    pattern: /\b(lists|listed|listing day|debut\w*)\b/i,
  },
];

// Most specific first: a story about allotment on the closing day is an allotment story
const BADGE_PRIORITY = ['allotment', 'post_listed', 'listed', 'closed', 'subscription', 'confirmed', 'upcoming'];
const GENERAL_BADGE = { key: 'general', label: 'IPO News', badge: 'bg-slate-50 text-slate-600 border-slate-200' };

export const newsTabByKey = (key) => NEWS_DESK_TABS.find((tab) => tab.key === key) || NEWS_DESK_TABS[0];

// Anything about a public issue: the page leaves out general market stories the search feed also returns
const IPO_PATTERN =
  /\b(ipos?|initial public offering|public issue|listing|listed|drhp|rhp|gmp|grey market|subscri\w*|allot(ment|ted)?|anchor (book|investors?)|price band|sme platform|nse emerge|bse sme)\b/i;

export const isIpoRelated = (article) => IPO_PATTERN.test(`${article.title} ${article.snippet || ''}`);

export const categorizeNews = (article) => {
  const text = `${article.title} ${article.snippet || ''}`;
  const tabs = NEWS_DESK_TABS.filter((tab) => tab.pattern && tab.pattern.test(text)).map((tab) => tab.key);
  const primaryKey = BADGE_PRIORITY.find((key) => tabs.includes(key));
  const primary = primaryKey ? NEWS_DESK_TABS.find((tab) => tab.key === primaryKey) : GENERAL_BADGE;
  return { tabs, category: { key: primary.key, label: primary.label, badge: primary.badge } };
};

// Articles from utils/newsMapper.js mapNewsItems -> desk rows
export const buildNewsDeskRows = (articles) =>
  (articles || []).map((article) => ({ ...article, ...categorizeNews(article), ipoRelated: isIpoRelated(article) }));

export const NEWS_SORTS = [
  { key: 'latest', label: 'Latest first' },
  { key: 'oldest', label: 'Oldest first' },
];

export const filterNewsDesk = (rows, { tab = 'all', query = '', sort = 'latest', includeUnrelated = false } = {}) => {
  const needle = query.trim().toLowerCase();
  const direction = sort === 'oldest' ? 1 : -1;
  return rows
    .filter((row) => includeUnrelated || row.ipoRelated)
    .filter((row) => tab === 'all' || row.tabs.includes(tab))
    .filter((row) => !needle || `${row.title} ${row.snippet} ${row.source}`.toLowerCase().includes(needle))
    .sort((a, b) => direction * (a.timestamp - b.timestamp));
};

export const countNewsTabs = (rows, { includeUnrelated = false } = {}) => {
  const pool = rows.filter((row) => includeUnrelated || row.ipoRelated);
  return Object.fromEntries(
    NEWS_DESK_TABS.map((tab) => [tab.key, tab.key === 'all' ? pool.length : pool.filter((row) => row.tabs.includes(tab.key)).length])
  );
};

const IST_DATE_TIME = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

// '2026-09-22T09:36:00+00:00' -> '22 Sept 2026, 15:06 IST'
export const formatPublishedIst = (iso) => {
  const date = new Date(iso);
  if (!iso || Number.isNaN(date.getTime())) return null;
  return `${IST_DATE_TIME.format(date)} IST`;
};

// Two-letter mark for stories without a thumbnail: "Business Standard" -> "BS"
export const sourceInitials = (source) =>
  String(source || 'News')
    .replace(/[^A-Za-z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');
